import DataTable from 'components/DataTable'
import ConfirmDialogModal from 'components/Modals/ConfirmDialog'
import { ICouponItem } from 'components/Modals/Coupons/types'
import ErrorModal from 'components/Modals/ErrorModal'
import Translation from 'components/Translation'
import {
    DEFAULT_ERROR_STATE,
    IError,
    useAppContext,
} from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import React, { useEffect, useState } from 'react'
import CouponEmptyPage from 'components/EmptyComponents/EmptyPage'
import { classNames } from 'utils/dom-helpers'
import { postApiRequest, putApiRequest, useFetch } from 'utils/fetch-helper'
import Dashboard from '..'
import GenericError from 'components/GenericError/GenericError'
import { getCurrencyCode, toYmd } from 'utils/string-localization-helper'
import { useTenantContext } from 'context/TenantContext'
import moment from 'moment-timezone'
import { DisplayTitle } from 'utils/display-title-helper'
import { formatDate } from 'utils/string-helper'
export const DISCOUNT_TYPE = {
    EXACT_VALUE: 'EXACT_VALUE',
    PERCENT: 'PERCENT',
}
export const COUPON_STATUS = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    EXPIRED: 'EXPIRED',
    REDEEMED: 'FULLY REDEEMED',
}

function Coupons() {
    const { CreateCouponModal } = useAuth()
    const { tenant, refetchCoupons, setRefetchCoupons } = useTenantContext()
    const { translations } = useAppContext()
    const [selected_items, selectItems] = useState<number[]>([])
    const [all_selected, selectAll] = useState<boolean>(false)
    const [coupons_list, setCouponsList] = useState<ICouponItem[]>([])
    const [pageSize, setPageSize] = useState(20)
    const [currentPage, setCurrentPage] = useState(0)
    const [displayConfirmationDialog, setDisplayConfirmationDialog] =
        useState('')

    const [errorMsg, setErrorMsg] = useState<IError>(DEFAULT_ERROR_STATE)
    const [successMsg, setSuccessMsg] = useState('')

    //TODO: refactor to use the array from the tenant context
    const { data: api_coupons_list, doFetch: getCoupons } = useFetch(
        `/v1/coupons/by-tenant-id?tenantId=${tenant?.id}&page=${currentPage}&size=${pageSize}`
    )

    useEffect(() => {
        if (refetchCoupons) {
            getCoupons()
        }

        setRefetchCoupons(false)
    }, [refetchCoupons])

    useEffect(() => {
        setCouponsList(transformResponse())
    }, [api_coupons_list])

    useEffect(() => {
        const update_selection: number[] = []
        if (all_selected) {
            api_coupons_list?.forEach((_: any, idx: number) => {
                update_selection.push(idx)
            })
        }
        selectItems(update_selection)
    }, [all_selected])

    const transformResponse = () => {
        let mapped = []
        if (api_coupons_list.length > 0) {
            mapped = api_coupons_list
                ?.filter((item: any) => !item?.deletedAt)
                .map((item: any) => ({
                    id: item?.id,
                    code: item?.code,
                    name: item?.name,
                    effectiveDate: item?.effectiveDate,
                    expiryDate: item?.expiryDate,
                    discountValue: item?.discountValue,
                    discountType:
                        item?.discountType || DISCOUNT_TYPE.PERCENT,
                    locationIds: item?.locationIds,
                    servicesIds: item?.serviceIds,
                    selected_services: item?.serviceIds,
                    categoriesIds: item?.categoryIds,
                    description: item?.description,
                    usageLimit: item?.usageLimit,
                    usagePerClient: item?.usagePerClient,
                    status: item?.status,
                    membership_ids: item?.membershipIds,
                }))
        }

        return mapped as ICouponItem[]
    }

    function toggleAll() {
        selectAll(!all_selected)
    }

    const handleArchiveAll = () => {
        if (selected_items.length > 0 && selected_items.length !== 1)
            setDisplayConfirmationDialog(
                'Are you sure to remove all selected coupons?'
            )
        else if (selected_items.length == 1) {
            setDisplayConfirmationDialog(
                `Are you sure you want to remove ${
                    coupons_list[selected_items[0]].code
                } coupon?`
            )
        }
    }

    const handleEdit = React.useCallback(
        (idx: number) => {
            return () => {
                CreateCouponModal.setAttributes({
                    id: coupons_list[idx]?.id,
                    code: coupons_list[idx]?.code,
                    name: coupons_list[idx]?.description,
                    effectiveDate: coupons_list[idx]?.effectiveDate,
                    expiryDate: coupons_list[idx]?.expiryDate,
                    discountValue: coupons_list[idx]?.discountValue,
                    discountType: coupons_list[idx]?.discountType,
                    locationIds: coupons_list[idx]?.locationIds,
                    servicesIds: coupons_list[idx]?.servicesIds,
                    categoriesIds: coupons_list[idx]?.categoriesIds,
                    selected_categories: coupons_list[idx]?.categoriesIds,
                    selected_services:
                        coupons_list[idx].selected_services ||
                        coupons_list[idx].membership_ids, //TODO: refactor and clean
                    description: coupons_list[idx]?.description,
                    operationType: 'edit',
                    usagePerClient: coupons_list[idx].usagePerClient,
                    usageLimit: coupons_list[idx].usageLimit,
                    status: coupons_list[idx].status,
                    membership_ids: coupons_list[idx].membership_ids,
                })
                CreateCouponModal.open()
            }
        },
        [coupons_list]
    )

    const handleArchiveConfirm = async () => {
        const removed = selected_items.map((item) =>
            putApiRequest(
                `/v1/coupons/soft-delete/${coupons_list[item].id}`,
                item
            )
        )
        const res = await Promise.all(removed)

        const check = res.some((item) => !item.ok)

        if (check) {
            const err = res.filter((item) => !item.ok)
            const defaultMsg = 'Error occurred while deleting coupon'
            setErrorMsg({
                message: err.length > 0 ? err[0].message : defaultMsg,
                tag: err[0]?.tag,
            })
        } else {
            setSuccessMsg('Successfully deleted a coupon!')
        }
        selectItems([])
        selectAll(false)
        getCoupons()
    }

    const handleArchiveCancel = () => {
        setDisplayConfirmationDialog('')
    }

    const transformRequest = (attributes: any) => {
        const payload = {
            categoryIds: attributes?.categoriesIds,
            code: `${attributes?.code}-NEW`,
            discountType: attributes?.discountType || 'PERCENT',
            discountValue: attributes?.discountValue,
            effectiveDate: attributes?.effectiveDate || toYmd(new Date()),
            expiryDate: attributes?.expiryDate,
            id: attributes?.id || '',
            locationIds: attributes?.locationIds,
            name: attributes?.name,
            serviceIds: attributes?.selected_services,
            tenantId: tenant?.id,
            usageLimit: attributes?.usageLimit || 0,
            usagePerClient: attributes?.usagePerClient || 1000,
            description: attributes?.description,
        }

        return payload
    }

    const handleDuplicate = async () => {
        const duplicate = selected_items.map((item) => {
            const payload = transformRequest(coupons_list[item])

            delete payload.id

            return postApiRequest(`/v1/coupons`, {
                ...payload,
            })
        })
        const res = await Promise.all(duplicate)
        const check = res.every((item) => item.ok)
        if (check) {
            setSuccessMsg('Successfully duplicated selected coupons')
        } else {
            const err = res.filter((item) => !item.ok)
            const defaultMsg = 'Error occurred while deleting coupon'
            setErrorMsg({
                message: err.length > 0 ? err[0].message : defaultMsg,
                tag: err[0]?.tag,
            })
        }
        selectItems([])
        selectAll(false)
        getCoupons()
    }

    const getCouponStatusClass = (status: string) => {
        let res = ''
        switch (status) {
            case COUPON_STATUS.ACTIVE:
                res = 'bg-green-100 text-green-600 '
                break
            case COUPON_STATUS.INACTIVE:
                res = 'bg-gray-100 text-gray-600 '
                break
            case COUPON_STATUS.EXPIRED:
                res = 'bg-sky-100 text-sky-600 '
                break
            case COUPON_STATUS.REDEEMED:
                res = 'bg-red-100 text-red-600 '
                break
            default:
                res = 'bg-black'
        }
        return res
    }

    const renderRows = () => {
        let rows = {}
        api_coupons_list?.length
            ? (rows = coupons_list?.map((item, index) => (
                  <tr
                      key={item.id}
                      className={classNames(
                          index % 2 ? 'bg-gray-50' : '',
                          'hover:bg-blue-300 hover:bg-opacity-30 cursor-pointer'
                      )}
                  >
                      <td className="px-5 py-4 align-middle text-gray-900 whitespace-nowrap">
                          <input
                              type="checkbox"
                              name="selected_items[]"
                              className="h-6 w-6 border-gray-300 rounded text-primary focus:ring-primary-light disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-three"
                              value={item.id}
                              checked={selected_items.indexOf(index) >= 0}
                              onChange={() => {
                                  const updated_selection = [
                                      ...selected_items,
                                  ]
                                  const idx =
                                      updated_selection.indexOf(index)
                                  if (idx >= 0) {
                                      updated_selection.splice(idx, 1)
                                  } else {
                                      updated_selection.push(index)
                                  }
                                  selectItems(updated_selection)
                              }}
                          />
                      </td>
                      <td
                          className="px-5 py-4 align-middle text-gray-900 whitespace-nowrap"
                          onClick={handleEdit(index)}
                      >
                          <div className="text-sm  text-gray-900">
                              {item.code}
                          </div>
                      </td>
                      <td
                          className="px-5 py-4 align-middle text-gray-900 whitespace-nowrap"
                          onClick={handleEdit(index)}
                      >
                          <div className="text-sm text-gray-900">
                              {item.description}
                          </div>
                      </td>{' '}
                      <td
                          className="px-5 py-4 align-middle text-gray-900 whitespace-nowrap"
                          onClick={handleEdit(index)}
                      >
                          <div className="text-sm text-gray-900">
                              {item.discountValue} &nbsp;
                              {item.discountType ===
                              DISCOUNT_TYPE.EXACT_VALUE
                                  ? getCurrencyCode()
                                  : '%'}
                          </div>
                      </td>
                      <td
                          className="px-5 py-4 align-middle text-gray-900 whitespace-nowrap"
                          onClick={handleEdit(index)}
                      >
                          <div className="text-sm text-gray-900">
                              {item.expiryDate &&
                                  formatDate(new Date(item.expiryDate))}
                          </div>
                      </td>
                      <td
                          className="px-5 py-4 align-middle text-gray-900 whitespace-nowrap"
                          onClick={handleEdit(index)}
                      >
                          <span
                              className={classNames(
                                  'px-2 py-1 text-[10px] font-medium rounded-full',
                                  getCouponStatusClass(item?.status)
                              )}
                          >
                              {item?.status}
                          </span>
                      </td>
                  </tr>
              )))
            : (rows = (
                  <tr
                      className={classNames(
                          // index % 2 ? 'bg-gray-50' : '',
                          'hover:bg-blue-300 hover:bg-opacity-30 cursor-pointer'
                      )}
                  >
                      <td
                          colSpan={6}
                          className="px-5 py-4 align-middle text-gray-900 whitespace-nowrap"
                      >
                          <span>
                              There are no coupons added yet. Click on the
                              <b> Add New </b>button to get started.
                          </span>
                      </td>
                  </tr>
              ))

        return rows as unknown as HTMLTableRowElement[]
    }

    const getCouponsTable = () => (
        <div className="overflow-hidden border rounded-lg mt-4">
            <DataTable
                all_selected={all_selected}
                rows={renderRows()}
                columns={[
                    {
                        classNames:
                            'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
                    },
                    {
                        label: translations['coupon_code'] || 'COUPON CODE',
                        classNames:
                            'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
                    },
                    {
                        label: translations['description'] || 'DESCRIPTION',
                        classNames:
                            'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
                    },
                    {
                        label: translations['discount'] || 'DISCOUNT',
                        classNames:
                            'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
                    },
                    {
                        label: translations['expiry_date'] || 'EXPIRY DATE',
                        classNames:
                            'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
                    },
                    {
                        label: translations['status'] || 'STATUS',
                        classNames:
                            'px-5 py-3 text-left text-xs text-gray-500 uppercase tracking-wider whitespace-nowrap',
                    },
                ]}
            />
        </div>
    )

    return (
        <Dashboard>
            <div className="flex flex-col">
                {/* {!api_coupons_list?.length ? (
                    <CouponEmptyPage
                        imageSrc="/images/empty_state.svg"
                        text={[
                            'There are no coupons added yet.',
                            'Click on the button below to get started.',
                        ]}
                        buttonLabel="Add Coupon"
                        onButtonClick={() => {
                            CreateCouponModal.setAttributes({
                                operationType: 'add',
                            })
                            CreateCouponModal.open()
                        }}
                    />
                ) : ( */}
                <div className="align-middle inline-block min-w-full ">
                    <div className="flex justify-between items-center page-title">
                        <div className="">{DisplayTitle()}</div>
                        <button
                            onClick={() => {
                                CreateCouponModal.setAttributes({
                                    operationType: 'add',
                                })
                                CreateCouponModal.open()
                            }}
                            className="flex items-center bg-custom-dark-cyan-blue text-white text-sm aot-primary-btn"
                        >
                            <i className="feather-plus mr-1" />
                            <Translation
                                content_key="add_new"
                                translations={translations}
                            />
                        </button>
                    </div>
                    <div className="pagebg-white">
                        <div className="flex gap-3 py-2 items-center">
                            <input
                                type="checkbox"
                                className="h-6 w-6 ml-2 border-gray-300 rounded text-primary focus:ring-primary-light"
                                onChange={toggleAll}
                                checked={all_selected}
                            />
                            <button
                                onClick={handleArchiveAll}
                                className="inline-flex justify-between items-center rounded-md border px-3 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <i className="feather-trash-2 mr-2" />
                                <Translation
                                    content_key="delete"
                                    translations={translations}
                                />
                            </button>
                            <button
                                disabled={selected_items.length === 0}
                                onClick={handleDuplicate}
                                className="inline-flex justify-between items-center rounded-md border px-3 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50"
                            >
                                <i className="feather-copy mr-2" />
                                <Translation
                                    content_key="duplicate"
                                    translations={translations}
                                />
                            </button>
                        </div>

                        {getCouponsTable()}
                    </div>
                </div>
                {/* )} */}

                {successMsg && (
                    <ErrorModal
                        error_message={successMsg}
                        onCloseModal={() => {
                            setSuccessMsg('')
                        }}
                        type="success"
                    />
                )}
                <ConfirmDialogModal
                    prompt_message={displayConfirmationDialog}
                    onConfirm={handleArchiveConfirm}
                    onCancel={handleArchiveCancel}
                />
                {!!errorMsg.message.length && (
                    <GenericError
                        error_message={errorMsg.message}
                        onCloseModal={() => {
                            setErrorMsg(DEFAULT_ERROR_STATE)
                        }}
                        tag={errorMsg?.tag}
                    />
                )}
            </div>
        </Dashboard>
    )
}

export default Coupons
